import { Fragment, type ReactNode } from 'react';

/**
 * A deliberately small Markdown renderer for event pages. It builds React
 * elements (never HTML strings), so text typed by an admin cannot inject
 * markup or scripts. Supported:
 *   ## Heading, ### Subheading
 *   - bullet, 1. numbered
 *   **bold**, *italic*, [link text](https://… | mailto:… | tel:…)
 *   blank line = new paragraph; single line break kept inside a paragraph
 */

const SAFE_URL = /^(https?:\/\/|mailto:|tel:)/i;

export function inline(text: string, key = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0, m: RegExpExecArray | null, n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${n++}`;
    if (m[1] !== undefined) out.push(<strong key={k}>{inline(m[1], k)}</strong>);
    else if (m[2] !== undefined) out.push(<em key={k}>{inline(m[2], k)}</em>);
    else if (m[3] !== undefined && m[4] !== undefined) {
      out.push(SAFE_URL.test(m[4])
        ? <a key={k} href={m[4]} target={m[4].startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="text-brand-700 underline">{m[3]}</a>
        : m[3]);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ source }: { source: string }) {
  const blocks: ReactNode[] = [];
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (para.length) {
      const k = `p${blocks.length}`;
      blocks.push(<p key={k}>{para.map((l, i) => <Fragment key={i}>{i > 0 && <br />}{inline(l, `${k}-${i}`)}</Fragment>)}</p>);
      para = [];
    }
    if (list) {
      const k = `l${blocks.length}`;
      const items = list.items.map((it, i) => <li key={i}>{inline(it, `${k}-${i}`)}</li>);
      blocks.push(list.ordered ? <ol key={k} className="list-decimal pl-6">{items}</ol> : <ul key={k} className="list-disc pl-6">{items}</ul>);
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{2,3})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) { flush(); continue; }
    if (h) {
      flush();
      const k = `h${blocks.length}`;
      blocks.push(h[1] === '##'
        ? <h2 key={k} className="mt-7 text-2xl first:mt-0">{inline(h[2]!, k)}</h2>
        : <h3 key={k} className="mt-4 text-lg">{inline(h[2]!, k)}</h3>);
      continue;
    }
    if (bullet || num) {
      const ordered = !!num;
      if (para.length || (list && list.ordered !== ordered)) flush();
      list ??= { ordered, items: [] };
      list.items.push((bullet ?? num)![1]!);
      continue;
    }
    if (list) flush();
    para.push(line);
  }
  flush();
  return <div className="space-y-3 text-[17px] leading-relaxed text-ink-900">{blocks}</div>;
}
